<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <!-- Page Hero -->
      <section class="page-hero">
        <div class="container">
          <div class="hero-content">
            <nav class="breadcrumb-nav">
              <a href="/" class="breadcrumb-link">Home</a>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">Water Services</span>
            </nav>
            <h1 class="page-title">Water Services</h1>
            <p class="page-subtitle">Apply for water connections, pay bills, and access water-related services</p>
          </div>

          <!-- Progress Steps -->
          <div class="progress-stepper" v-if="currentStep < 4">
            <div 
              v-for="(step, index) in steps" 
              :key="index"
              class="step-item"
              :class="{ 
                active: currentStep === index + 1, 
                completed: currentStep > index + 1 
              }"
            >
              <div class="step-connector" v-if="index > 0"></div>
              <div class="step-circle">
                <svg v-if="currentStep > index + 1" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span v-else>{{ index + 1 }}</span>
              </div>
              <span class="step-label">{{ step }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Main Content -->
      <section class="content-section">
        <div class="container">
          <div class="content-grid">
            <!-- Left Side - Info Panel -->
            <div class="info-panel">
              <div class="info-card">
                <div class="info-card-header">
                  <div class="info-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                      <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"></path>
                    </svg>
                  </div>
                  <h3>Water Services</h3>
                </div>
                <p class="info-description">
                  Access clean and safe water services for your home or business. Apply for new connections, pay bills, and manage your water account.
                </p>

                <div class="info-features">
                  <div class="feature-item">
                    <div class="feature-icon green">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"></path>
                      </svg>
                    </div>
                    <div>
                      <strong>New Connections</strong>
                      <p>Apply for water connection</p>
                    </div>
                  </div>

                  <div class="feature-item">
                    <div class="feature-icon blue">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                        <line x1="2" y1="10" x2="22" y2="10"></line>
                      </svg>
                    </div>
                    <div>
                      <strong>Bill Payment</strong>
                      <p>Pay water bills online</p>
                    </div>
                  </div>

                  <div class="feature-item">
                    <div class="feature-icon purple">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
                      </svg>
                    </div>
                    <div>
                      <strong>Repairs & Maintenance</strong>
                      <p>Request service repairs</p>
                    </div>
                  </div>
                </div>

                <div class="who-needs">
                  <h4>Services Available</h4>
                  <ul>
                    <li>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      New water connection
                    </li>
                    <li>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      Meter installation
                    </li>
                    <li>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      Reconnection services
                    </li>
                    <li>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      Water bowser services
                    </li>
                    <li>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      Exhauster services
                    </li>
                  </ul>
                </div>

                <div class="help-box">
                  <h4>Need Help?</h4>
                  <p>Contact our water services department for assistance.</p>
                  <a href="/contact" class="help-link">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                    </svg>
                    Contact Support
                  </a>
                </div>
              </div>
            </div>

            <!-- Right Side - Form -->
            <div class="form-panel">
              <!-- Step 1: Customer Details -->
              <div v-if="currentStep === 1" class="form-card">
                <div class="form-card-header">
                  <div class="step-indicator">Step 1 of 3</div>
                  <h2>Customer Details</h2>
                  <p>Enter your details and service location</p>
                </div>

                <div class="form-card-body">
                  <!-- Customer Info -->
                  <div class="form-section">
                    <h3 class="section-title">
                      <span class="section-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                          <circle cx="12" cy="7" r="4"></circle>
                        </svg>
                      </span>
                      Personal Information
                    </h3>

                    <div class="form-row">
                      <div class="form-group">
                        <label class="form-label">Full Name <span class="required">*</span></label>
                        <input type="text" v-model="customer.fullName" class="form-input" placeholder="Enter your full name"/>
                      </div>
                      <div class="form-group">
                        <label class="form-label">ID/Passport Number</label>
                        <input type="text" v-model="customer.idNumber" class="form-input" placeholder="Enter ID number"/>
                      </div>
                    </div>

                    <div class="form-row">
                      <div class="form-group">
                        <label class="form-label">Phone Number <span class="required">*</span></label>
                        <input type="tel" v-model="customer.phone" class="form-input" placeholder="07XX XXX XXX"/>
                      </div>
                      <div class="form-group">
                        <label class="form-label">Email Address</label>
                        <input type="email" v-model="customer.email" class="form-input" placeholder="email@example.com"/>
                      </div>
                    </div>
                  </div>

                  <!-- Location -->
                  <div class="form-section">
                    <h3 class="section-title">
                      <span class="section-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                          <circle cx="12" cy="10" r="3"></circle>
                        </svg>
                      </span>
                      Service Location
                    </h3>

                    <div class="form-row">
                      <div class="form-group">
                        <label class="form-label">Sub County <span class="required">*</span></label>
                        <div class="select-wrapper">
                          <select v-model="selectedSubCounty" class="form-select">
                            <option :value="null">-- Select Sub County --</option>
                            <option v-for="sc in subCounties" :key="sc.subCountyID" :value="sc">{{ sc.subCountyName }}</option>
                          </select>
                          <span class="select-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg></span>
                        </div>
                      </div>
                      <div class="form-group">
                        <label class="form-label">Ward <span class="required">*</span></label>
                        <div class="select-wrapper">
                          <select v-model="selectedWard" class="form-select" :disabled="!selectedSubCounty">
                            <option :value="null">-- Select Ward --</option>
                            <option v-for="w in wards" :key="w.wardID" :value="w">{{ w.wardName }}</option>
                          </select>
                          <span class="select-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg></span>
                        </div>
                      </div>
                    </div>

                    <div class="form-row">
                      <div class="form-group">
                        <label class="form-label">Plot Number</label>
                        <input type="text" v-model="customer.plotNumber" class="form-input" placeholder="Enter plot number"/>
                      </div>
                      <div class="form-group">
                        <label class="form-label">Water Account Number (if existing)</label>
                        <input type="text" v-model="customer.accountNumber" class="form-input" placeholder="Enter account number"/>
                      </div>
                    </div>

                    <div class="form-group">
                      <label class="form-label">Physical Address <span class="required">*</span></label>
                      <textarea v-model="customer.address" class="form-textarea" rows="2" placeholder="Enter building name, street, landmark"></textarea>
                    </div>
                  </div>

                  <div v-if="error" class="error-message">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>
                    {{ error }}
                  </div>

                  <div class="form-actions">
                    <button type="button" class="btn btn-primary" @click="proceedToStep2" :disabled="!canProceedStep1">
                      Continue to Select Services
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Step 2: Select Services -->
              <div v-else-if="currentStep === 2" class="form-card">
                <div class="form-card-header">
                  <div class="step-indicator">Step 2 of 3</div>
                  <h2>Select Services</h2>
                  <p>Choose the water services you need</p>
                </div>

                <div class="form-card-body">
                  <div class="summary-card compact">
                    <div class="summary-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg></div>
                    <div class="summary-details">
                      <strong>{{ customer.fullName }}</strong>
                      <span>{{ selectedWard?.wardName }}, {{ selectedSubCounty?.subCountyName }}</span>
                    </div>
                    <button type="button" class="edit-btn" @click="currentStep = 1">Edit</button>
                  </div>

                  <div v-if="loadingServices" class="loading-box">
                    <span class="spinner"></span>
                    <p>Loading available services...</p>
                  </div>

                  <div v-else class="services-section">
                    <div v-for="category in incomeTypes" :key="category.incomeTypeId" class="service-category">
                      <div class="category-header" @click="toggleCategory(category)" :class="{ expanded: expandedCategories.includes(category.incomeTypeId) }">
                        <div class="category-info">
                          <div class="category-icon water"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"></path></svg></div>
                          <div>
                            <h4>{{ category.incomeTypeDescription }}</h4>
                            <span class="category-count">{{ getCategoryFees(category.incomeTypeId).length }} services available</span>
                          </div>
                        </div>
                        <div class="category-toggle">
                          <span class="selected-count" v-if="getSelectedCountForCategory(category.incomeTypeId) > 0">{{ getSelectedCountForCategory(category.incomeTypeId) }} selected</span>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline :points="expandedCategories.includes(category.incomeTypeId) ? '18 15 12 9 6 15' : '6 9 12 15 18 9'"></polyline></svg>
                        </div>
                      </div>

                      <div v-show="expandedCategories.includes(category.incomeTypeId)" class="category-fees">
                        <div v-if="loadingFees[category.incomeTypeId]" class="loading-fees"><span class="spinner small"></span>Loading...</div>
                        <div v-else-if="getCategoryFees(category.incomeTypeId).length === 0" class="no-fees">No services available in this category</div>
                        <div v-else class="fees-grid">
                          <label v-for="fee in getCategoryFees(category.incomeTypeId)" :key="fee.feeId" class="fee-item" :class="{ selected: isSelected(fee) }">
                            <input type="checkbox" :checked="isSelected(fee)" @change="toggleFee(fee)" class="hidden-checkbox"/>
                            <span class="checkbox-mark"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"></polyline></svg></span>
                            <div class="fee-details">
                              <span class="fee-name">{{ fee.feeDescription }}</span>
                              <span class="fee-price">KES {{ formatAmount(fee.unitFeeAmount) }}</span>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-if="selectedFees.length > 0" class="selected-summary">
                    <div class="summary-header">
                      <h4>Selected Services ({{ selectedFees.length }})</h4>
                      <button type="button" class="clear-btn" @click="clearAll">Clear All</button>
                    </div>
                    <div class="selected-list">
                      <div v-for="fee in selectedFees" :key="fee.feeId" class="selected-item">
                        <span class="item-name">{{ fee.feeDescription }}</span>
                        <span class="item-price">KES {{ formatAmount(fee.unitFeeAmount) }}</span>
                        <button type="button" class="remove-btn" @click="toggleFee(fee)"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg></button>
                      </div>
                    </div>
                    <div class="total-row">
                      <span>Total Amount</span>
                      <strong>KES {{ formatAmount(totalAmount) }}</strong>
                    </div>
                  </div>

                  <div v-if="error" class="error-message"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>{{ error }}</div>

                  <div class="form-actions dual">
                    <button type="button" class="btn btn-secondary" @click="currentStep = 1"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>Back</button>
                    <button type="button" class="btn btn-primary" @click="currentStep = 3" :disabled="selectedFees.length === 0">Continue to Review<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg></button>
                  </div>
                </div>
              </div>

              <!-- Step 3: Review & Submit -->
              <div v-else-if="currentStep === 3" class="form-card">
                <div class="form-card-header">
                  <div class="step-indicator">Step 3 of 3</div>
                  <h2>Review & Submit</h2>
                  <p>Review your details before submitting</p>
                </div>

                <div class="form-card-body">
                  <div class="review-section">
                    <div class="review-header">
                      <h3>Customer Details</h3>
                      <button type="button" class="edit-btn" @click="currentStep = 1">Edit</button>
                    </div>
                    <div class="review-grid">
                      <div class="review-item"><span class="label">Full Name</span><span class="value">{{ customer.fullName }}</span></div>
                      <div class="review-item"><span class="label">Phone Number</span><span class="value">{{ customer.phone }}</span></div>
                      <div class="review-item" v-if="customer.idNumber"><span class="label">ID Number</span><span class="value">{{ customer.idNumber }}</span></div>
                      <div class="review-item" v-if="customer.email"><span class="label">Email</span><span class="value">{{ customer.email }}</span></div>
                      <div class="review-item" v-if="customer.accountNumber"><span class="label">Account Number</span><span class="value">{{ customer.accountNumber }}</span></div>
                      <div class="review-item" v-if="customer.plotNumber"><span class="label">Plot Number</span><span class="value">{{ customer.plotNumber }}</span></div>
                      <div class="review-item full"><span class="label">Service Location</span><span class="value">{{ customer.address }}, {{ selectedWard?.wardName }}, {{ selectedSubCounty?.subCountyName }}</span></div>
                    </div>
                  </div>

                  <div class="review-section">
                    <div class="review-header">
                      <h3>Selected Services</h3>
                      <button type="button" class="edit-btn" @click="currentStep = 2">Edit</button>
                    </div>
                    <div class="invoice-preview">
                      <table class="invoice-table">
                        <thead><tr><th>Service Description</th><th class="text-right">Amount</th></tr></thead>
                        <tbody><tr v-for="fee in selectedFees" :key="fee.feeId"><td>{{ fee.feeDescription }}</td><td class="text-right">KES {{ formatAmount(fee.unitFeeAmount) }}</td></tr></tbody>
                        <tfoot><tr><th>Total Payable</th><th class="text-right">KES {{ formatAmount(totalAmount) }}</th></tr></tfoot>
                      </table>
                    </div>
                  </div>

                  <div class="payment-notice">
                    <div class="notice-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg></div>
                    <div class="notice-content">
                      <strong>Payment Information</strong>
                      <p>After submission, an invoice will be generated and sent to your phone via SMS. Use the invoice number to make payment via M-Pesa.</p>
                    </div>
                  </div>

                  <label class="declaration-check">
                    <input type="checkbox" v-model="declarationAccepted" />
                    <span class="checkmark"></span>
                    <span class="declaration-text">I confirm that the information provided is accurate and I agree to the terms of service for water services.</span>
                  </label>

                  <div v-if="error" class="error-message"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>{{ error }}</div>

                  <div class="form-actions dual">
                    <button type="button" class="btn btn-secondary" @click="currentStep = 2"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>Back</button>
                    <button type="button" class="btn btn-primary" @click="submitApplication" :disabled="!declarationAccepted || submitting">
                      <span v-if="submitting" class="spinner white small"></span>
                      <span v-else>Submit & Generate Invoice</span>
                      <svg v-if="!submitting" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Step 4: Success -->
              <div v-else-if="currentStep === 4" class="form-card success-card">
                <div class="success-content">
                  <div class="success-icon"><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg></div>
                  <h2>Invoice Generated!</h2>
                  <p>Your water service request has been processed successfully.</p>

                  <div class="invoice-card">
                    <span class="invoice-label">Invoice Number</span>
                    <strong class="invoice-number">{{ billDetails?.billNo }}</strong>
                    <div class="invoice-amount"><span>Total Amount</span><strong>KES {{ formatAmount(billDetails?.detailAmount) }}</strong></div>
                  </div>

                  <div class="notification-card">
                    <div class="notification-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg></div>
                    <div class="notification-text"><strong>SMS Sent!</strong><p>Invoice details have been sent to {{ customer.phone }}</p></div>
                  </div>

                  <div class="next-steps">
                    <h4>How to Pay</h4>
                    <ul>
                      <li><span class="step-num">1</span><div><strong>Go to M-Pesa</strong><p>Select Lipa na M-Pesa > Paybill</p></div></li>
                      <li><span class="step-num">2</span><div><strong>Enter Paybill Number</strong><p>Business No: {{ county?.paybillNumber || '123456' }}</p></div></li>
                      <li><span class="step-num">3</span><div><strong>Enter Account Number</strong><p>Use Invoice: {{ billDetails?.billNo }}</p></div></li>
                    </ul>
                  </div>

                  <div class="success-actions">
                    <button type="button" class="btn btn-secondary" @click="printInvoice"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>Print Invoice</button>
                    <button type="button" class="btn btn-primary" @click="resetForm"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>New Request</button>
                    <a href="/" class="btn btn-outline">Back to Home</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <FooterComponent />
  </div>
</template>

<script>
import { biller, execute } from "@/api";
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";

export default {
  name: 'WaterServices',
  components: { HeaderComponent, FooterComponent },

  data() {
    return {
      currentStep: 1,
      steps: ['Customer Details', 'Select Services', 'Review & Submit'],
      error: '',
      county: null,
      customer: { fullName: '', idNumber: '', phone: '', email: '', address: '', plotNumber: '', accountNumber: '' },
      subCounties: [],
      wards: [],
      selectedSubCounty: null,
      selectedWard: null,
      incomeTypes: [],
      categoryFees: {},
      selectedFees: [],
      expandedCategories: [],
      loadingServices: false,
      loadingFees: {},
      declarationAccepted: false,
      submitting: false,
      billDetails: null,
      billInfo: []
    };
  },

  computed: {
    canProceedStep1() {
      return this.customer.fullName && this.customer.phone && this.selectedSubCounty && this.selectedWard && this.customer.address;
    },
    totalAmount() {
      return this.selectedFees.reduce((sum, fee) => sum + parseFloat(fee.unitFeeAmount || 0), 0);
    }
  },

  watch: {
    selectedSubCounty(newVal) {
      if (newVal) {
        this.getWards(newVal.subCountyID);
        this.selectedWard = null;
      }
    }
  },

  mounted() {
    this.getCounty();
    this.getSubCounties();
  },

  methods: {
    formatAmount(amount) {
      if (!amount) return '0';
      return parseFloat(amount).toLocaleString('en-KE', { minimumFractionDigits: 0 });
    },

    async getCounty() {
      try {
        const data = new FormData();
        data.append("function", "getCounty");
        const res = await execute(data, biller);
        if (res.data.success) this.county = res.data.data.county;
      } catch (e) { console.error(e); }
    },

    async getSubCounties() {
      try {
        const data = new FormData();
        data.append("function", "getSubCounty");
        const res = await execute(data, biller);
        if (res.data.success) this.subCounties = res.data.data.subCounties;
      } catch (e) { console.error(e); }
    },

    async getWards(subCountyID) {
      try {
        const data = new FormData();
        data.append("function", "getWards");
        data.append("subCountyID", subCountyID);
        const res = await execute(data, biller);
        if (res.data.success) this.wards = res.data.data.wards;
      } catch (e) { console.error(e); }
    },

    async getIncomeTypes() {
      this.loadingServices = true;
      try {
        const data = new FormData();
        data.append("function", "getIncomeTypes");
        data.append("incomeTypePrefix", "WTR");
        const res = await execute(data, biller);
        if (res.data.success) {
          this.incomeTypes = res.data.data.incomeTypes;
          if (this.incomeTypes.length > 0) this.toggleCategory(this.incomeTypes[0]);
        }
      } catch (e) { 
        console.error(e);
        this.error = 'Failed to load services. Please try again.';
      } finally {
        this.loadingServices = false;
      }
    },

    async getFeesForCategory(incomeTypeId) {
      if (this.categoryFees[incomeTypeId]) return;
      this.loadingFees = { ...this.loadingFees, [incomeTypeId]: true };
      try {
        const data = new FormData();
        data.append("function", "getFeesAndCharges");
        data.append("incomeTypeId", incomeTypeId);
        const res = await execute(data, biller);
        if (res.data.success) this.categoryFees = { ...this.categoryFees, [incomeTypeId]: res.data.data.feesAndCharges };
      } catch (e) { console.error(e); }
      finally { this.loadingFees = { ...this.loadingFees, [incomeTypeId]: false }; }
    },

    toggleCategory(category) {
      const index = this.expandedCategories.indexOf(category.incomeTypeId);
      if (index >= 0) {
        this.expandedCategories.splice(index, 1);
      } else {
        this.expandedCategories.push(category.incomeTypeId);
        if (!this.categoryFees[category.incomeTypeId]) this.getFeesForCategory(category.incomeTypeId);
      }
    },

    getCategoryFees(incomeTypeId) { return this.categoryFees[incomeTypeId] || []; },
    getSelectedCountForCategory(incomeTypeId) { return this.getCategoryFees(incomeTypeId).filter(fee => this.isSelected(fee)).length; },
    isSelected(fee) { return this.selectedFees.some(f => f.feeId === fee.feeId); },
    
    toggleFee(fee) {
      const index = this.selectedFees.findIndex(f => f.feeId === fee.feeId);
      if (index >= 0) this.selectedFees.splice(index, 1);
      else this.selectedFees.push({ ...fee });
    },

    clearAll() { this.selectedFees = []; },

    proceedToStep2() {
      if (!this.canProceedStep1) return;
      this.currentStep = 2;
      if (this.incomeTypes.length === 0) this.getIncomeTypes();
    },

    async submitApplication() {
      if (!this.declarationAccepted || this.selectedFees.length === 0) return;
      this.submitting = true;
      this.error = '';

      try {
        const billItems = this.selectedFees.map(fee => ({
          feeId: fee.feeId,
          feeDescription: fee.feeDescription,
          unitFeeAmount: fee.unitFeeAmount,
          accountNo: fee.accountNo,
          costCenterNo: fee.costCenterNo,
          incomeTypeId: fee.incomeTypeId,
          amount: fee.unitFeeAmount,
          quantity: 1,
          customer: this.customer.fullName,
          revenueStreamItem: fee.feeDescription,
          subCountyID: this.selectedSubCounty.subCountyID,
          subCountyName: this.selectedSubCounty.subCountyName,
          wardID: this.selectedWard.wardID,
          wardName: this.selectedWard.wardName,
          idNo: this.customer.idNumber || '',
          phoneNumber: this.customer.phone,
          names: this.customer.fullName,
          customerPhoneNumber: this.customer.phone,
          description: `Water Services - ${this.customer.address}${this.customer.accountNumber ? ' | Acc: ' + this.customer.accountNumber : ''}`
        }));

        const data = new FormData();
        data.append("function", "generateBill3");
        data.append("billItem", JSON.stringify(billItems));
        const res = await execute(data, biller);

        if (res.data.success) {
          this.billDetails = res.data.data.bill.billDetails;
          this.billInfo = res.data.data.bill.billInfo;
          this.currentStep = 4;
        } else {
          this.error = res.data.message || 'Failed to generate invoice. Please try again.';
        }
      } catch (e) {
        this.error = 'Error submitting request. Please try again.';
        console.error(e);
      } finally {
        this.submitting = false;
      }
    },

    printInvoice() {
      if (!this.billDetails || !this.billDetails.billNo) return;
      localStorage.setItem("params", JSON.stringify({ billNo: this.billDetails.billNo }));
      const routeData = this.$router.resolve({ name: 'print-invoice' });
      window.open(routeData.href, '_blank');
    },

    resetForm() {
      this.currentStep = 1;
      this.customer = { fullName: '', idNumber: '', phone: '', email: '', address: '', plotNumber: '', accountNumber: '' };
      this.selectedSubCounty = null;
      this.selectedWard = null;
      this.selectedFees = [];
      this.expandedCategories = [];
      this.declarationAccepted = false;
      this.billDetails = null;
      this.error = '';
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;700&display=swap');

.page-wrapper { font-family: 'Plus Jakarta Sans', sans-serif; background: #f5f7fa; min-height: 100vh; }
.main-content { padding-bottom: 80px; }
.container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }

.page-hero { background: linear-gradient(135deg, #0369a1 0%, #0284c7 50%, #0c4a6e 100%); padding: 48px 0 60px; position: relative; overflow: hidden; }
.page-hero::before { content: ''; position: absolute; top: 0; right: 0; width: 500px; height: 500px; background: radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%); transform: translate(30%, -30%); }
.hero-content { text-align: center; margin-bottom: 40px; }

.breadcrumb-nav { display: flex; align-items: center; justify-content: center; gap: 8px; margin-bottom: 16px; }
.breadcrumb-link { color: rgba(255,255,255,0.7); text-decoration: none; font-size: 14px; transition: color 0.2s; }
.breadcrumb-link:hover { color: white; }
.breadcrumb-separator { color: rgba(255,255,255,0.4); }
.breadcrumb-current { color: white; font-size: 14px; }

.page-title { font-size: 36px; font-weight: 800; color: white; margin: 0 0 12px; letter-spacing: -0.5px; }
.page-subtitle { font-size: 16px; color: rgba(255,255,255,0.8); margin: 0; max-width: 500px; margin: 0 auto; }

.progress-stepper { display: flex; justify-content: center; align-items: center; gap: 0; max-width: 600px; margin: 0 auto; }
.step-item { display: flex; align-items: center; }
.step-connector { width: 60px; height: 2px; background: rgba(255,255,255,0.2); margin: 0 8px; }
.step-item.completed .step-connector { background: #10b981; }
.step-circle { width: 40px; height: 40px; border-radius: 50%; background: rgba(255,255,255,0.15); border: 2px solid rgba(255,255,255,0.3); display: flex; align-items: center; justify-content: center; color: rgba(255,255,255,0.6); font-weight: 700; font-size: 14px; transition: all 0.3s; }
.step-item.active .step-circle { background: white; border-color: white; color: #0369a1; transform: scale(1.1); box-shadow: 0 4px 15px rgba(0,0,0,0.2); }
.step-item.completed .step-circle { background: #10b981; border-color: #10b981; color: white; }
.step-label { margin-left: 12px; font-size: 14px; font-weight: 600; color: rgba(255,255,255,0.6); white-space: nowrap; }
.step-item.active .step-label { color: white; }
.step-item.completed .step-label { color: rgba(255,255,255,0.8); }

.content-section { margin-top: -30px; position: relative; z-index: 10; }
.content-grid { display: grid; grid-template-columns: 320px 1fr; gap: 32px; align-items: start; }

.info-panel { position: sticky; top: 24px; }
.info-card { background: white; border-radius: 16px; padding: 28px; box-shadow: 0 4px 20px rgba(0,0,0,0.08); }
.info-card-header { display: flex; align-items: center; gap: 16px; margin-bottom: 20px; padding-bottom: 20px; border-bottom: 1px solid #e2e8f0; }
.info-icon { width: 56px; height: 56px; background: linear-gradient(135deg, #0369a1 0%, #0284c7 100%); border-radius: 14px; display: flex; align-items: center; justify-content: center; color: white; }
.info-card-header h3 { font-size: 20px; font-weight: 700; color: #1e293b; margin: 0; }
.info-description { font-size: 14px; color: #64748b; line-height: 1.6; margin-bottom: 24px; }

.info-features { display: flex; flex-direction: column; gap: 16px; margin-bottom: 24px; }
.feature-item { display: flex; gap: 14px; align-items: flex-start; }
.feature-icon { width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.feature-icon.green { background: #d1fae5; color: #059669; }
.feature-icon.blue { background: #dbeafe; color: #2563eb; }
.feature-icon.purple { background: #ede9fe; color: #7c3aed; }
.feature-item strong { display: block; font-size: 14px; font-weight: 600; color: #1e293b; }
.feature-item p { font-size: 12px; color: #64748b; margin: 2px 0 0; }

.who-needs { background: #f0f9ff; border-radius: 12px; padding: 20px; margin-bottom: 24px; }
.who-needs h4 { font-size: 14px; font-weight: 700; color: #1e293b; margin: 0 0 14px; }
.who-needs ul { list-style: none; padding: 0; margin: 0; }
.who-needs li { display: flex; align-items: center; gap: 10px; font-size: 13px; color: #475569; padding: 8px 0; }
.who-needs li svg { color: #0284c7; flex-shrink: 0; }

.help-box { background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%); border-radius: 12px; padding: 20px; }
.help-box h4 { font-size: 14px; font-weight: 700; color: #0c4a6e; margin: 0 0 8px; }
.help-box p { font-size: 13px; color: #0369a1; margin: 0 0 16px; }
.help-link { display: inline-flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; color: #0284c7; text-decoration: none; }
.help-link:hover { text-decoration: underline; }

.form-panel { min-height: 500px; }
.form-card { background: white; border-radius: 20px; box-shadow: 0 10px 40px rgba(0,0,0,0.1); overflow: hidden; }
.form-card-header { background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%); padding: 28px 32px; border-bottom: 1px solid #bae6fd; }
.step-indicator { display: inline-block; font-size: 12px; font-weight: 600; color: #0369a1; background: #bae6fd; padding: 6px 14px; border-radius: 20px; margin-bottom: 12px; }
.form-card-header h2 { font-size: 24px; font-weight: 800; color: #1e293b; margin: 0 0 6px; }
.form-card-header p { font-size: 14px; color: #64748b; margin: 0; }
.form-card-body { padding: 32px; }

.form-section { margin-bottom: 32px; }
.section-title { display: flex; align-items: center; gap: 12px; font-size: 16px; font-weight: 700; color: #1e293b; margin: 0 0 20px; padding-bottom: 12px; border-bottom: 2px solid #e2e8f0; }
.section-icon { width: 36px; height: 36px; background: #e0f2fe; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: #0369a1; }

.form-row { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-bottom: 20px; }
.form-group { display: flex; flex-direction: column; gap: 8px; }
.form-label { font-size: 13px; font-weight: 600; color: #374151; }
.required { color: #ef4444; }
.form-input, .form-select, .form-textarea { padding: 14px 16px; border: 1.5px solid #e2e8f0; border-radius: 10px; font-size: 14px; font-family: inherit; transition: all 0.2s; background: white; color: #1e293b; }
.form-input:focus, .form-select:focus, .form-textarea:focus { outline: none; border-color: #0284c7; box-shadow: 0 0 0 3px rgba(3, 105, 161, 0.1); }
.form-input::placeholder, .form-textarea::placeholder { color: #94a3b8; }
.form-textarea { resize: vertical; min-height: 80px; }
.select-wrapper { position: relative; }
.select-wrapper .form-select { appearance: none; padding-right: 40px; cursor: pointer; width: 100%; }
.select-icon { position: absolute; right: 14px; top: 50%; transform: translateY(-50%); color: #64748b; pointer-events: none; }

.loading-box { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 60px 20px; text-align: center; }
.loading-box p { margin-top: 16px; color: #64748b; }
.spinner { width: 40px; height: 40px; border: 3px solid #e2e8f0; border-top-color: #0284c7; border-radius: 50%; animation: spin 0.8s linear infinite; }
.spinner.small { width: 20px; height: 20px; border-width: 2px; }
.spinner.white { border-color: rgba(255,255,255,0.3); border-top-color: white; }
@keyframes spin { to { transform: rotate(360deg); } }

.summary-card { display: flex; align-items: center; gap: 16px; padding: 16px 20px; background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%); border: 1px solid #86efac; border-radius: 12px; margin-bottom: 24px; }
.summary-card.compact { padding: 14px 18px; }
.summary-icon { width: 44px; height: 44px; background: #059669; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: white; flex-shrink: 0; }
.summary-details { flex: 1; }
.summary-details strong { display: block; font-size: 15px; font-weight: 700; color: #166534; }
.summary-details span { font-size: 13px; color: #15803d; }
.edit-btn { padding: 8px 16px; background: white; border: 1px solid #86efac; border-radius: 8px; font-size: 13px; font-weight: 600; color: #059669; cursor: pointer; transition: all 0.2s; }
.edit-btn:hover { background: #f0fdf4; }

.services-section { display: flex; flex-direction: column; gap: 16px; }
.service-category { border: 1px solid #e2e8f0; border-radius: 14px; overflow: hidden; background: white; }
.category-header { display: flex; align-items: center; justify-content: space-between; padding: 18px 20px; cursor: pointer; transition: background 0.2s; }
.category-header:hover { background: #f0f9ff; }
.category-header.expanded { background: #e0f2fe; border-bottom: 1px solid #bae6fd; }
.category-info { display: flex; align-items: center; gap: 14px; }
.category-icon { width: 44px; height: 44px; background: linear-gradient(135deg, #0f4c81 0%, #1a5a96 100%); border-radius: 10px; display: flex; align-items: center; justify-content: center; color: white; }
.category-icon.water { background: linear-gradient(135deg, #0369a1 0%, #0284c7 100%); }
.category-info h4 { font-size: 15px; font-weight: 700; color: #1e293b; margin: 0 0 4px; }
.category-count { font-size: 12px; color: #64748b; }
.category-toggle { display: flex; align-items: center; gap: 12px; color: #64748b; }
.selected-count { font-size: 12px; font-weight: 600; color: #0284c7; background: #bae6fd; padding: 4px 10px; border-radius: 20px; }

.category-fees { padding: 20px; background: #f8fafc; }
.loading-fees { display: flex; align-items: center; gap: 10px; color: #64748b; font-size: 14px; padding: 10px 0; }
.no-fees { color: #94a3b8; font-size: 14px; text-align: center; padding: 20px; }
.fees-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 12px; }

.fee-item { display: flex; align-items: flex-start; gap: 14px; padding: 16px; background: white; border: 1.5px solid #e2e8f0; border-radius: 12px; cursor: pointer; transition: all 0.2s; }
.fee-item:hover { border-color: #0284c7; background: #f0f9ff; }
.fee-item.selected { border-color: #0284c7; background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%); }
.hidden-checkbox { position: absolute; opacity: 0; pointer-events: none; }
.checkbox-mark { width: 22px; height: 22px; border: 2px solid #d1d5db; border-radius: 6px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all 0.2s; background: white; }
.fee-item.selected .checkbox-mark { background: #0284c7; border-color: #0284c7; color: white; }
.fee-details { flex: 1; }
.fee-name { display: block; font-size: 14px; font-weight: 600; color: #1e293b; margin-bottom: 4px; }
.fee-price { display: block; font-size: 15px; font-weight: 700; color: #0369a1; }

.selected-summary { background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%); border: 1px solid #bae6fd; border-radius: 14px; padding: 20px; margin-top: 24px; }
.summary-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.summary-header h4 { font-size: 16px; font-weight: 700; color: #1e293b; margin: 0; }
.clear-btn { font-size: 13px; font-weight: 600; color: #dc2626; background: none; border: none; cursor: pointer; }
.clear-btn:hover { text-decoration: underline; }

.selected-list { display: flex; flex-direction: column; gap: 10px; max-height: 200px; overflow-y: auto; }
.selected-item { display: flex; align-items: center; gap: 12px; padding: 12px 16px; background: white; border-radius: 10px; border: 1px solid #e2e8f0; }
.item-name { flex: 1; font-size: 14px; color: #1e293b; }
.item-price { font-size: 14px; font-weight: 700; color: #0369a1; }
.remove-btn { width: 28px; height: 28px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 6px; display: flex; align-items: center; justify-content: center; color: #dc2626; cursor: pointer; transition: all 0.2s; }
.remove-btn:hover { background: #fee2e2; }

.total-row { display: flex; justify-content: space-between; align-items: center; margin-top: 16px; padding-top: 16px; border-top: 2px solid #bae6fd; }
.total-row span { font-size: 14px; color: #64748b; }
.total-row strong { font-size: 20px; font-weight: 800; color: #0369a1; }

.review-section { margin-bottom: 28px; }
.review-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; padding-bottom: 12px; border-bottom: 2px solid #e2e8f0; }
.review-header h3 { font-size: 16px; font-weight: 700; color: #1e293b; margin: 0; }
.review-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
.review-item { background: #f8fafc; padding: 14px 18px; border-radius: 10px; }
.review-item.full { grid-column: 1 / -1; }
.review-item .label { display: block; font-size: 11px; font-weight: 600; color: #64748b; text-transform: uppercase; margin-bottom: 4px; }
.review-item .value { font-size: 14px; font-weight: 600; color: #1e293b; }

.invoice-preview { background: #f8fafc; border-radius: 12px; overflow: hidden; }
.invoice-table { width: 100%; border-collapse: collapse; }
.invoice-table th, .invoice-table td { padding: 14px 18px; text-align: left; }
.invoice-table thead { background: linear-gradient(135deg, #0369a1 0%, #0284c7 100%); color: white; }
.invoice-table thead th { font-size: 13px; font-weight: 600; }
.invoice-table tbody tr { border-bottom: 1px solid #e2e8f0; }
.invoice-table tbody td { font-size: 14px; color: #1e293b; }
.invoice-table tfoot { background: linear-gradient(135deg, #059669 0%, #047857 100%); color: white; }
.invoice-table tfoot th { font-size: 15px; font-weight: 700; }
.text-right { text-align: right; }

.payment-notice { display: flex; align-items: flex-start; gap: 16px; padding: 20px; background: #e0f2fe; border: 1px solid #7dd3fc; border-radius: 12px; margin: 24px 0; }
.notice-icon { width: 44px; height: 44px; background: #0284c7; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: white; flex-shrink: 0; }
.notice-content strong { display: block; font-size: 14px; font-weight: 700; color: #0c4a6e; margin-bottom: 4px; }
.notice-content p { font-size: 13px; color: #0369a1; margin: 0; line-height: 1.5; }

.declaration-check { display: flex; align-items: flex-start; gap: 14px; padding: 20px; background: #fffbeb; border: 1px solid #fcd34d; border-radius: 12px; cursor: pointer; margin-bottom: 24px; }
.declaration-check input { position: absolute; opacity: 0; }
.checkmark { width: 24px; height: 24px; border: 2px solid #d97706; border-radius: 6px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all 0.2s; background: white; }
.declaration-check input:checked + .checkmark { background: #d97706; border-color: #d97706; }
.declaration-check input:checked + .checkmark::after { content: '✓'; color: white; font-weight: bold; font-size: 14px; }
.declaration-text { font-size: 14px; color: #92400e; line-height: 1.5; }

.error-message { display: flex; align-items: center; gap: 12px; padding: 16px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 10px; color: #dc2626; font-size: 14px; margin-bottom: 20px; }

.form-actions { display: flex; gap: 16px; margin-top: 24px; }
.form-actions.dual { justify-content: space-between; }
.btn { display: inline-flex; align-items: center; justify-content: center; gap: 10px; padding: 16px 28px; border-radius: 12px; font-size: 15px; font-weight: 700; cursor: pointer; transition: all 0.3s; border: none; font-family: inherit; }
.btn-primary { background: linear-gradient(135deg, #0369a1 0%, #0284c7 100%); color: white; box-shadow: 0 4px 15px rgba(3, 105, 161, 0.3); }
.btn-primary:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(3, 105, 161, 0.4); }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
.btn-secondary { background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; }
.btn-secondary:hover { background: #e2e8f0; }
.btn-outline { background: transparent; color: #0369a1; border: 2px solid #0369a1; text-decoration: none; }
.btn-outline:hover { background: #0369a1; color: white; }

.success-card { text-align: center; }
.success-content { padding: 48px 32px; }
.success-icon { width: 80px; height: 80px; background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 24px; color: #059669; }
.success-content h2 { font-size: 28px; font-weight: 800; color: #059669; margin: 0 0 12px; }
.success-content > p { font-size: 15px; color: #64748b; margin: 0 0 32px; }

.invoice-card { background: linear-gradient(135deg, #0369a1 0%, #0c4a6e 100%); border-radius: 14px; padding: 24px; color: white; margin-bottom: 24px; }
.invoice-label { font-size: 12px; opacity: 0.8; text-transform: uppercase; }
.invoice-number { display: block; font-size: 24px; font-weight: 800; margin: 8px 0 16px; letter-spacing: 1px; }
.invoice-amount { display: flex; justify-content: space-between; padding-top: 16px; border-top: 1px solid rgba(255,255,255,0.2); }
.invoice-amount span { opacity: 0.8; }
.invoice-amount strong { font-size: 20px; }

.notification-card { display: flex; align-items: flex-start; gap: 16px; padding: 20px; background: #fef3c7; border: 1px solid #fcd34d; border-radius: 12px; margin-bottom: 24px; text-align: left; }
.notification-icon { width: 44px; height: 44px; background: #fbbf24; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: white; flex-shrink: 0; }
.notification-text strong { display: block; color: #92400e; margin-bottom: 4px; }
.notification-text p { font-size: 14px; color: #a16207; margin: 0; }

.next-steps { text-align: left; margin-bottom: 32px; }
.next-steps h4 { font-size: 16px; font-weight: 600; color: #1e293b; margin-bottom: 16px; text-align: center; }
.next-steps ul { list-style: none; padding: 0; margin: 0; }
.next-steps li { display: flex; gap: 16px; padding: 16px; background: #f8fafc; border-radius: 10px; margin-bottom: 8px; }
.step-num { width: 28px; height: 28px; background: #0369a1; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 13px; flex-shrink: 0; }
.next-steps li strong { display: block; color: #1e293b; margin-bottom: 2px; }
.next-steps li p { font-size: 13px; color: #64748b; margin: 0; }

.success-actions { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }

@media (max-width: 1024px) {
  .content-grid { grid-template-columns: 1fr; }
  .info-panel { position: static; }
  .progress-stepper { flex-wrap: wrap; gap: 16px; }
  .step-connector { display: none; }
}

@media (max-width: 768px) {
  .form-card-body { padding: 24px; }
  .form-row { grid-template-columns: 1fr; }
  .review-grid { grid-template-columns: 1fr; }
  .fees-grid { grid-template-columns: 1fr; }
  .success-actions { flex-direction: column; }
  .page-title { font-size: 26px; }
  .form-actions { flex-direction: column; }
  .form-actions.dual { flex-direction: column-reverse; }
}
</style>
